import type { ToolState, ToolStatus } from "../types";
import type { McpToolPreview, ToolProposal, ToolResultPayload } from "./sseEvents";
import type { Message } from "./thread";

const TERMINAL_STATUSES: ReadonlySet<string> = new Set([
  "succeeded",
  "failed",
  "declined",
  "expired",
]);

/**
 * Map a persisted message's `tool_calls` to render-ready ToolState entries so
 * reloaded conversations show the same tool cards the live run did. Persisted
 * calls are terminal — they render the compact, button-less variant ("approved"
 * never persists as a final state, but degrade it to "succeeded"-adjacent
 * "executing" → treat as failed-silent: we simply skip non-terminal rows).
 */
export function historicalToolStates(message: Message): ToolState[] {
  const calls = message.tool_calls ?? [];
  const out: ToolState[] = [];
  for (const call of calls) {
    if (!TERMINAL_STATUSES.has(call.status)) continue;
    const proposal: ToolProposal = {
      tool_call_id: call.id,
      tool_name: call.name,
      args: call.arguments ?? {},
      credit_cost: call.credit_cost ?? 0,
      ...(call.title ? { tool_title: call.title } : {}),
      ...(call.connector_id
        ? {
            connector: {
              id: call.connector_id,
              name: call.connector_name ?? call.connector_slug ?? "",
              ...(call.connector_slug ? { slug: call.connector_slug } : {}),
            },
          }
        : {}),
    };
    out.push({
      callId: call.id,
      proposal,
      status: call.status as ToolStatus,
      // Persisted result/preview payloads thread through so reloads re-render
      // the same outcome cards the live run did (old rows lack both → null).
      result: (call.result ?? null) as ToolResultPayload | null,
      preview: (call.preview ?? null) as McpToolPreview | null,
      resultPreview: call.result_preview ?? null,
      error: call.error ?? null,
      autoApproved: true, // terminal: compact card, no buttons
    });
  }
  return out;
}
